const productRoots = document.querySelectorAll('[data-product-root]');

function formatMoney(cents, product) {
  const amount = Number(cents || 0) / 100;
  const format = product.moneyFormat || '${{amount}}';
  const placeholder = format.match(/\{\{\s*(amount(?:_no_decimals)?(?:_with_(?:comma|apostrophe)_separator)?)\s*\}\}/);
  if (!placeholder) {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: product.currency }).format(amount);
  }
  const token = placeholder[1];
  const noDecimals = token.includes('no_decimals');
  const commaSeparator = token.includes('comma_separator');
  const apostropheSeparator = token.includes('apostrophe_separator');
  let formatted = amount.toFixed(noDecimals ? 0 : 2);
  let [whole, decimals] = formatted.split('.');
  const thousands = apostropheSeparator ? "'" : commaSeparator ? '.' : ',';
  const decimal = commaSeparator ? ',' : '.';
  whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  formatted = noDecimals ? whole : `${whole}${decimal}${decimals}`;
  return format.replace(placeholder[0], formatted);
}

function initGallery(root) {
  const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
  const panels = [...root.querySelectorAll('[data-gallery-media]')];
  const stage = root.querySelector('[data-gallery-stage]');
  if (!thumbs.length || !panels.length) return;

  let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true'));

  const show = (index, focus = false) => {
    activeIndex = (index + thumbs.length) % thumbs.length;
    thumbs.forEach((thumb, thumbIndex) => {
      if (thumbIndex === activeIndex) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    panels.forEach((panel, panelIndex) => { panel.hidden = panelIndex !== activeIndex; });
    thumbs[activeIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    if (focus) thumbs[activeIndex]?.focus({ preventScroll: true });
  };

  thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index)));
  root.querySelector('[data-gallery-previous]')?.addEventListener('click', () => show(activeIndex - 1));
  root.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(activeIndex + 1));

  let touchStart = 0;
  stage?.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0]?.clientX || 0; }, { passive: true });
  stage?.addEventListener('touchend', (event) => {
    const distance = (event.changedTouches[0]?.clientX || 0) - touchStart;
    if (Math.abs(distance) > 45) show(activeIndex + (distance < 0 ? 1 : -1));
  }, { passive: true });

  return { showMedia(mediaId) { const index = thumbs.findIndex((thumb) => thumb.dataset.mediaId === String(mediaId)); if (index >= 0) show(index); } };
}

function initProduct(root) {
  if (root.dataset.productReady) return;
  root.dataset.productReady = 'true';
  const dataElement = root.querySelector('[data-product-json]');
  if (!dataElement) return;
  const product = JSON.parse(dataElement.textContent);
  product.currency ||= root.dataset.currency;
  product.moneyFormat ||= root.dataset.moneyFormat;
  const variants = product.variants || [];
  const master = root.querySelector('[data-master-variant]');
  const form = root.querySelector('[data-product-form]');
  const add = root.querySelector('[data-add-button]');
  const addLabel = root.querySelector('[data-add-label]');
  const addPrice = root.querySelector('[data-add-price]');
  const addSeparator = root.querySelector('[data-add-separator]');
  const saleSummary = root.querySelector('[data-product-price-summary]');
  const salePrice = root.querySelector('[data-product-sale-price]');
  const comparePrice = root.querySelector('[data-product-compare-price]');
  const genericPrice = root.querySelector('[id^="ProductPrice-"]');
  const afterpay = root.querySelector('[data-afterpay-amount]');
  const gallery = initGallery(root);
  let selectedVariant = variants.find((variant) => String(variant.id) === String(master?.value)) || variants[0];

  const normalOptions = [...root.querySelectorAll('[data-product-option]')];
  const abControl = root.querySelector('[data-product-ab]');
  const genericSelects = normalOptions.filter((input) => input.tagName === 'SELECT');

  function selectedValues() {
    if (genericSelects.length) return genericSelects.map((select) => select.value);
    const values = [];
    normalOptions.filter((input) => input.checked).forEach((input) => { values[Number(input.dataset.optionPosition) - 1] = input.value; });
    if (abControl) values[Number(abControl.dataset.optionPosition) - 1] = abControl.checked ? abControl.dataset.upgradeValue : abControl.dataset.regularValue;
    return values;
  }

  function findVariant(values) {
    return variants.find((variant) => variant.options.every((value, index) => value === values[index]));
  }

  function isAvailableWith(index, value, selections) {
    return variants.some((variant) => variant.available && variant.options[index] === value && variant.options.every((optionValue, optionIndex) => optionIndex === index || optionValue === selections[optionIndex]));
  }

  function updateAvailability(selections) {
    normalOptions.filter((input) => input.type === 'radio').forEach((input) => {
      const index = Number(input.dataset.optionPosition) - 1;
      input.disabled = !isAvailableWith(index, input.value, selections);
      input.nextElementSibling?.classList.toggle('is-unavailable', input.disabled);
    });
    if (abControl) {
      const index = Number(abControl.dataset.optionPosition) - 1;
      const upgradeAvailable = isAvailableWith(index, abControl.dataset.upgradeValue, selections);
      abControl.disabled = !upgradeAvailable && !abControl.checked;
      abControl.closest('label')?.classList.toggle('is-unavailable', abControl.disabled);
    }
  }

  function updateAbPrice(selections) {
    const price = root.querySelector('[data-ab-price]');
    if (!abControl || !price) return;
    const index = Number(abControl.dataset.optionPosition) - 1;
    const regularValues = [...selections];
    const upgradeValues = [...selections];
    regularValues[index] = abControl.dataset.regularValue;
    upgradeValues[index] = abControl.dataset.upgradeValue;
    const regularVariant = findVariant(regularValues);
    const upgradeVariant = findVariant(upgradeValues);
    const difference = Number(upgradeVariant?.price || 0) - Number(regularVariant?.price || 0);
    price.textContent = difference > 0 ? `(+${formatMoney(difference, product)})` : '';
  }

  function renderVariant(variant, selections) {
    selectedVariant = variant;
    if (master && variant) master.value = variant.id;
    const available = Boolean(variant?.available);
    if (add) add.disabled = !available;
    if (addLabel) addLabel.textContent = available ? 'Add to cart' : variant ? 'Sold out' : 'Unavailable';
    [addPrice, addSeparator].forEach((element) => { if (element) element.hidden = !available; });
    if (addPrice && variant) addPrice.textContent = formatMoney(variant.price, product);
    if (afterpay && variant) afterpay.textContent = formatMoney(Math.round(variant.price / 4), product);

    const variantComparePrice = Number(variant?.compareAtPrice ?? variant?.compare_at_price ?? 0);
    const onSale = Boolean(variant && variantComparePrice > variant.price);
    if (saleSummary) saleSummary.hidden = !onSale;
    if (salePrice && variant) salePrice.textContent = formatMoney(variant.price, product);
    if (comparePrice && variant) comparePrice.textContent = onSale ? formatMoney(variantComparePrice, product) : '';
    if (genericPrice && variant) {
      const currentPrice = document.createElement('span');
      currentPrice.className = onSale ? 'price__sale' : '';
      currentPrice.textContent = formatMoney(variant.price, product);
      genericPrice.replaceChildren(currentPrice);
      if (onSale) {
        const previousPrice = document.createElement('s');
        previousPrice.className = 'price__compare';
        previousPrice.textContent = formatMoney(variantComparePrice, product);
        genericPrice.append(previousPrice);
      }
    }

    updateAvailability(selections);
    updateAbPrice(selections);
    const featuredMediaId = variant?.featuredMediaId ?? variant?.featured_media?.id;
    if (featuredMediaId) gallery?.showMedia(featuredMediaId);
    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  function selectVariant() {
    const selections = selectedValues();
    renderVariant(findVariant(selections), selections);
  }

  normalOptions.forEach((input) => input.addEventListener('change', selectVariant));
  abControl?.addEventListener('change', selectVariant);
  renderVariant(selectedVariant, selectedValues());

  form?.addEventListener('submit', async (event) => {
    if (!window.fetch || !window.PDCart?.refresh || !selectedVariant?.available) return;
    event.preventDefault();
    event.stopPropagation();
    const status = form.querySelector('[data-product-status]');
    const originalLabel = addLabel?.textContent;
    add?.setAttribute('aria-busy', 'true');
    if (add) add.disabled = true;
    if (addLabel) addLabel.textContent = 'Adding…';
    if (status) status.textContent = '';
    try {
      const response = await fetch(window.PDTheme.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
      await window.PDCart.refresh(true);
      if (status) status.textContent = 'Added to your cart.';
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      add?.removeAttribute('aria-busy');
      if (add) add.disabled = !selectedVariant?.available;
      if (addLabel) addLabel.textContent = selectedVariant?.available ? 'Add to cart' : originalLabel;
    }
  });
}

productRoots.forEach(initProduct);

function initRelated(scope = document) {
  scope.querySelectorAll('[data-product-related-carousel]').forEach((carousel) => {
    if (carousel.dataset.relatedReady) return;
    carousel.dataset.relatedReady = 'true';
    const track = carousel.querySelector('[data-related-track]');
    const items = [...carousel.querySelectorAll('.pd-related-product')];
    const previous = carousel.querySelector('[data-related-previous]');
    const next = carousel.querySelector('[data-related-next]');
    const indicator = carousel.querySelector('[data-related-indicator]');
    if (!track || !items.length || !previous || !next) return;
    const metrics = () => {
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      const step = items[0].getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.round((track.clientWidth + gap) / step));
      return { step, visible, pages: Math.max(1, Math.ceil(items.length / visible)) };
    };
    const currentPage = () => { const { step, visible, pages } = metrics(); return Math.min(pages - 1, Math.max(0, Math.round(track.scrollLeft / (step * visible)))); };
    const update = () => {
      const { pages } = metrics();
      const current = currentPage();
      previous.disabled = current === 0;
      next.disabled = current === pages - 1;
      if (indicator) indicator.textContent = `${current + 1} / ${pages}`;
    };
    const move = (direction) => { const { step, visible } = metrics(); track.scrollBy({ left: direction * step * visible, behavior: 'smooth' }); };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  });
}

initRelated();
document.addEventListener('pd:related-products-loaded', (event) => initRelated(event.detail || document));
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-product-root]').forEach(initProduct);
  initRelated(event.target);
});
